import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, AlertCircle, AlertTriangle, UserPlus, CheckCircle, XCircle, FileWarning } from 'lucide-react';
import api from '../lib/api';

const ICONS = {
    join_request: { icon: UserPlus, bg: 'bg-blue-100', color: 'text-blue-600' },
    member_joined: { icon: UserPlus, bg: 'bg-emerald-100', color: 'text-emerald-600' },
    request_approved: { icon: CheckCircle, bg: 'bg-emerald-100', color: 'text-emerald-600' },
    request_rejected: { icon: XCircle, bg: 'bg-red-100', color: 'text-red-600' },
    document_expiring: { icon: FileWarning, bg: 'bg-orange-100', color: 'text-orange-600' },
    document_expired: { icon: AlertCircle, bg: 'bg-red-100', color: 'text-red-600' },
};

export default function Notifications() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/notifications').then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
    }, []);

    const onItemClick = (n) => {
        if (n.type === 'join_request' && n.data?.request_id) {
            navigate('/familia/solicitudes');
        } else if (['document_expiring', 'document_expired'].includes(n.type) && n.data?.member_id) {
            navigate(`/familia/miembro/${n.data.member_id}?tab=documentos`);
        } else if (n.type === 'member_joined' && n.data?.member_id) {
            navigate(`/familia/miembro/${n.data.member_id}`);
        }
        if (n.id && !n.id.startsWith('exp_')) {
            api.post(`/notifications/${n.id}/read`).catch(() => {});
        }
    };

    return (
        <div className="min-h-screen bg-surface app-shell px-6 py-6">
            <div className="flex items-center justify-between mb-6">
                <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="notif-back">
                    <ArrowLeft className="w-5 h-5 text-brand-navy" />
                </button>
                <h1 className="text-lg font-bold text-brand-navy">Notificaciones</h1>
                <button
                    onClick={() => { api.post('/notifications/read-all').then(() => setItems((arr) => arr.map((x) => ({ ...x, read: true })))); }}
                    className="text-xs font-bold text-brand"
                    data-testid="notif-read-all"
                >
                    Marcar leídas
                </button>
            </div>

            {loading ? (
                <div className="text-center py-10 text-gray-400">Cargando...</div>
            ) : items.length === 0 ? (
                <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Bell className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-base font-bold text-brand-navy">Sin notificaciones</h3>
                    <p className="text-sm text-gray-500 mt-1">Te avisaremos cuando haya novedades.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((n) => {
                        const cfg = ICONS[n.type] || { icon: Bell, bg: 'bg-gray-100', color: 'text-gray-600' };
                        const I = cfg.icon;
                        return (
                            <button
                                key={n.id}
                                onClick={() => onItemClick(n)}
                                className={`w-full flex items-start gap-3 p-3 rounded-2xl text-left transition active:scale-[0.99] ${n.read ? 'bg-white' : 'bg-brand/5 border border-brand/15'} shadow-card`}
                                data-testid={`notification-${n.id}`}
                            >
                                <div className={`w-10 h-10 rounded-2xl ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                                    <I className={`w-5 h-5 ${cfg.color}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-brand-navy">{n.title}</div>
                                    <div className="text-xs text-gray-600 mt-0.5">{n.body}</div>
                                    {n.created_at && <div className="text-[11px] text-gray-400 mt-1">{new Date(n.created_at).toLocaleString('es')}</div>}
                                </div>
                                {!n.read && <span className="w-2 h-2 bg-brand rounded-full mt-2" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
